import { UpcomingEvent } from '@/lib/api/dashboard/dashboard-api'
import { format, isToday, isTomorrow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdArrowForward,
    MdCalendarToday,
    MdLocalHospital,
    MdMedicalServices,
} from 'react-icons/md'
import { Link } from 'react-router-dom'

interface UpcomingEventsWidgetProps {
    events: UpcomingEvent[]
    isHealth: boolean
}

const EVENT_ICONS = {
    consultation: MdLocalHospital,
    order: MdMedicalServices,
}

const EVENT_COLORS = {
    consultation: 'info',
    order: 'warning',
}

export default function UpcomingEventsWidget({
    events,
    isHealth,
}: UpcomingEventsWidgetProps) {
    const formatEventDate = (dateStr: string) => {
        if (!dateStr) return ''
        const date = parseISO(dateStr)
        if (isToday(date)) return `Hoje às ${format(date, 'HH:mm')}`
        if (isTomorrow(date)) return `Amanhã às ${format(date, 'HH:mm')}`
        return format(date, "dd/MM 'às' HH:mm", { locale: ptBR })
    }

    const getEventLink = (event: UpcomingEvent) => {
        switch (event.type) {
            case 'consultation':
                return `/bookings/consultations/${event.id}`
            case 'order':
            default:
                return `/bookings/orders/${event.id}`
        }
    }

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 overflow-hidden">
            <div className="bg-gradient-to-r from-secondary/10 to-secondary/10 px-5 py-4 border-b border-base-300/50">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-secondary/20">
                        <MdCalendarToday className="text-secondary text-lg" />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-base-content">
                            Próximos Eventos
                        </h3>
                        <p className="text-xs text-base-content/60">
                            Agendamentos e compromissos
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-4">
                {events.length === 0 ? (
                    <div className="text-center py-8 text-base-content/50">
                        <MdCalendarToday className="text-4xl mx-auto mb-2 opacity-50" />
                        <p>Nenhum evento agendado</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {events.slice(0, 5).map(event => {
                            const Icon = EVENT_ICONS[event.type]
                            const color = EVENT_COLORS[event.type]

                            return (
                                <Link
                                    key={`${event.type}-${event.id}`}
                                    to={getEventLink(event)}
                                    className={`group flex items-center gap-3 p-3 rounded-lg bg-base-200/50 hover:bg-${color}/10 border border-transparent hover:border-${color}/20 transition-all`}
                                >
                                    <div className={`p-2 rounded-lg bg-${color}/10`}>
                                        <Icon className={`text-${color}`} />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm text-base-content truncate">
                                            {event.title}
                                        </p>
                                        <p className="text-xs text-base-content/60 truncate">
                                            {event.clientName}
                                            {event.professionalName &&
                                                ` - ${event.professionalName}`}
                                        </p>
                                    </div>

                                    <div className="text-right">
                                        <p className="text-xs font-medium text-base-content">
                                            {formatEventDate(event.scheduledAt)}
                                        </p>
                                    </div>

                                    <MdArrowForward className="text-base-content/30 group-hover:text-base-content/60 transition-colors" />
                                </Link>
                            )
                        })}
                    </div>
                )}

                {events.length > 5 && (
                    <Link
                        to="/atendimento/agenda"
                        className="block text-center text-sm text-secondary hover:underline mt-4"
                    >
                        Ver todos ({events.length})
                    </Link>
                )}
            </div>
        </div>
    )
}
